import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import Program from "@/pages/Program";
import HowItWorks from "@/pages/HowItWorks";
import Apply from "@/pages/Apply";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Workspace from "@/pages/Workspace";
import { Privacy, Terms, Refunds, ProgramAgreement } from "@/pages/Legal";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import HubLayout from "@/pages/hub/HubLayout";
import { StartHere, MyProgress, Modules, ModuleDetail } from "@/pages/hub/HubCore";
import LessonPage from "@/pages/hub/LessonPage";
import { Templates, ImplementationDashboard, CoachingCalls, AskForHelp, Account, Graduation, HubSection } from "@/pages/hub/HubExtras";
import Admin from "@/pages/Admin";
function ModuleDetailRoute() {
  const { moduleId } = useParams();
  return <ModuleDetail moduleId={moduleId} />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function MarketingShell({ children }) {
  return (
    <>
      <Header />
      <ScrollToTop />
      {children}
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1.05 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<MarketingShell><Home /></MarketingShell>} />
            <Route path="/program" element={<MarketingShell><Program /></MarketingShell>} />
            <Route path="/how-it-works" element={<MarketingShell><HowItWorks /></MarketingShell>} />
            <Route path="/apply" element={<MarketingShell><Apply /></MarketingShell>} />
            <Route path="/about" element={<MarketingShell><About /></MarketingShell>} />
            <Route path="/faq" element={<MarketingShell><Faq /></MarketingShell>} />
            <Route path="/contact" element={<MarketingShell><Contact /></MarketingShell>} />
            <Route path="/login" element={<MarketingShell><Login /></MarketingShell>} />
            <Route path="/privacy" element={<MarketingShell><Privacy /></MarketingShell>} />
            <Route path="/terms" element={<MarketingShell><Terms /></MarketingShell>} />
            <Route path="/refunds" element={<MarketingShell><Refunds /></MarketingShell>} />
            <Route path="/program-agreement" element={<MarketingShell><ProgramAgreement /></MarketingShell>} />
            <Route path="/payment/success" element={<MarketingShell><PaymentSuccess /></MarketingShell>} />
            <Route path="/payment/cancel" element={<MarketingShell><PaymentCancel /></MarketingShell>} />
            <Route path="/hub" element={<HubLayout />}>
              <Route index element={<StartHere />} />
              <Route path="progress" element={<MyProgress />} />
              <Route path="modules" element={<Modules />} />
              <Route path="modules/:moduleId" element={<ModuleDetailRoute />} />
              <Route path="modules/:moduleId/lessons/:lessonId" element={<LessonPage />} />
              <Route path="templates" element={<Templates />} />
              <Route path="dashboard" element={<ImplementationDashboard />} />
              <Route path="calls" element={<CoachingCalls />} />
              <Route path="help" element={<AskForHelp />} />
              <Route path="day-1" element={<HubSection sectionKey="day-1" />} />
              <Route path="pipeline-engine" element={<HubSection sectionKey="pipeline-engine" />} />
              <Route path="partner" element={<HubSection sectionKey="partner" />} />
              <Route path="team-onboarding" element={<HubSection sectionKey="team-onboarding" />} />
              <Route path="account" element={<Account />} />
              <Route path="graduation" element={<Graduation />} />
            </Route>
            <Route path="/admin" element={<Admin />} />
            <Route path="/workspace" element={<Workspace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
